// Copyright (c) 2026 CTO's Club LLC. All rights reserved.

import { RE2JS } from 're2js';
import { z } from 'zod';
import {
    defineSafeIntegerDomain,
    normalizeStoredJsonSafeIntegerObjectFields,
} from './stored-json-number';

// One declarative chain of string rules for an RBS binding. On an output
// binding it shapes text read from the website before it fills its field; on
// an input binding it shapes a transaction-details value before it is
// written. The same pure function runs in RPA Builder, the backend validator
// and Samba, so a preview always shows what runs.

export const IpxRbsValueTransformOp = {
    TEXT_BEFORE: 'text_before',
    TEXT_AFTER: 'text_after',
    EXTRACT_TEXT: 'extract_text',
    REPLACE_TEXT: 'replace_text',
    REMOVE_TEXT: 'remove_text',
    TRIM: 'trim',
    COLLAPSE_SPACES: 'collapse_spaces',
    ADD_PREFIX: 'add_prefix',
    ADD_SUFFIX: 'add_suffix',
} as const;
export type IpxRbsValueTransformOp =
    (typeof IpxRbsValueTransformOp)[keyof typeof IpxRbsValueTransformOp];

export const IpxRbsValueTransformOccurrence = {
    FIRST: 'first',
    LAST: 'last',
} as const;
export type IpxRbsValueTransformOccurrence =
    (typeof IpxRbsValueTransformOccurrence)[keyof typeof IpxRbsValueTransformOccurrence];

export interface IpxRbsLiteralTextMatch {
    readonly is_regex: false;
    readonly text: string;
}

// A pattern for the linear-time RE2 engine: no backreferences or lookaround.
export interface IpxRbsRegexTextMatch {
    readonly is_regex: true;
    readonly pattern: string;
    readonly ignore_case: boolean;
}

export type IpxRbsTextMatch = IpxRbsLiteralTextMatch | IpxRbsRegexTextMatch;

export interface IpxRbsTextBeforeStep {
    readonly op: typeof IpxRbsValueTransformOp.TEXT_BEFORE;
    readonly separator: IpxRbsTextMatch;
    readonly occurrence: IpxRbsValueTransformOccurrence;
}

export interface IpxRbsTextAfterStep {
    readonly op: typeof IpxRbsValueTransformOp.TEXT_AFTER;
    readonly separator: IpxRbsTextMatch;
    readonly occurrence: IpxRbsValueTransformOccurrence;
}

// Keeps the whole match (group 0) or one capture group of a regex match.
export interface IpxRbsExtractTextStep {
    readonly op: typeof IpxRbsValueTransformOp.EXTRACT_TEXT;
    readonly match: IpxRbsRegexTextMatch;
    readonly group: number;
    readonly occurrence: IpxRbsValueTransformOccurrence;
}

// Replaces every occurrence; the replacement is always literal.
export interface IpxRbsReplaceTextStep {
    readonly op: typeof IpxRbsValueTransformOp.REPLACE_TEXT;
    readonly find: IpxRbsTextMatch;
    readonly replace_with: string;
}

export interface IpxRbsRemoveTextStep {
    readonly op: typeof IpxRbsValueTransformOp.REMOVE_TEXT;
    readonly find: IpxRbsTextMatch;
}

export interface IpxRbsTrimStep {
    readonly op: typeof IpxRbsValueTransformOp.TRIM;
}

export interface IpxRbsCollapseSpacesStep {
    readonly op: typeof IpxRbsValueTransformOp.COLLAPSE_SPACES;
}

export interface IpxRbsAddPrefixStep {
    readonly op: typeof IpxRbsValueTransformOp.ADD_PREFIX;
    readonly text: string;
}

export interface IpxRbsAddSuffixStep {
    readonly op: typeof IpxRbsValueTransformOp.ADD_SUFFIX;
    readonly text: string;
}

export type IpxRbsValueTransformStep =
    | IpxRbsTextBeforeStep
    | IpxRbsTextAfterStep
    | IpxRbsExtractTextStep
    | IpxRbsReplaceTextStep
    | IpxRbsRemoveTextStep
    | IpxRbsTrimStep
    | IpxRbsCollapseSpacesStep
    | IpxRbsAddPrefixStep
    | IpxRbsAddSuffixStep;

export interface IpxRbsValueTransformV1 {
    readonly transform_version: 1;
    readonly steps: readonly IpxRbsValueTransformStep[];
}

export const IpxRbsValueTransformSupportedVersions: readonly 1[] = [1];

export const IpxRbsValueTransformLimits = {
    MAX_STEPS: 8,
    MAX_LITERAL_LENGTH: 64,
    MAX_PATTERN_LENGTH: 200,
    MAX_CAPTURE_GROUP: 9,
    MAX_INPUT_LENGTH: 4_096,
    MAX_OUTPUT_LENGTH: 1_024,
} as const;

const LiteralTextSchema = z.string().min(1).max(IpxRbsValueTransformLimits.MAX_LITERAL_LENGTH);

export const IpxRbsLiteralTextMatchSchema: z.ZodType<IpxRbsLiteralTextMatch> = z
    .object({ is_regex: z.literal(false), text: LiteralTextSchema })
    .strict();

export const IpxRbsRegexTextMatchSchema: z.ZodType<IpxRbsRegexTextMatch> = z
    .object({
        is_regex: z.literal(true),
        pattern: z.string().min(1).max(IpxRbsValueTransformLimits.MAX_PATTERN_LENGTH),
        ignore_case: z.boolean(),
    })
    .strict()
    .superRefine((match, ctx) => {
        const compiled = compileRbsRegex(match);
        if (compiled.status === 'invalid') {
            ctx.addIssue({ code: 'custom', message: compiled.message, path: ['pattern'] });
            return;
        }
        if (compiled.regex.matcher('').find()) {
            ctx.addIssue({
                code: 'custom',
                message: 'Pattern must not match empty text.',
                path: ['pattern'],
            });
        }
    });

export const IpxRbsTextMatchSchema: z.ZodType<IpxRbsTextMatch> = z.union([
    IpxRbsLiteralTextMatchSchema,
    IpxRbsRegexTextMatchSchema,
]);

const OccurrenceSchema: z.ZodType<IpxRbsValueTransformOccurrence> = z.enum(
    IpxRbsValueTransformOccurrence
);

export const IpxRbsValueTransformStepSchema: z.ZodType<IpxRbsValueTransformStep> = z.union([
    z
        .object({
            op: z.literal(IpxRbsValueTransformOp.TEXT_BEFORE),
            separator: IpxRbsTextMatchSchema,
            occurrence: OccurrenceSchema,
        })
        .strict(),
    z
        .object({
            op: z.literal(IpxRbsValueTransformOp.TEXT_AFTER),
            separator: IpxRbsTextMatchSchema,
            occurrence: OccurrenceSchema,
        })
        .strict(),
    z
        .object({
            op: z.literal(IpxRbsValueTransformOp.EXTRACT_TEXT),
            match: IpxRbsRegexTextMatchSchema,
            group: z.number().int().min(0).max(IpxRbsValueTransformLimits.MAX_CAPTURE_GROUP),
            occurrence: OccurrenceSchema,
        })
        .strict()
        .superRefine((step, ctx) => {
            const compiled = compileRbsRegex(step.match);
            if (compiled.status === 'compiled' && step.group > compiled.regex.groupCount()) {
                ctx.addIssue({
                    code: 'custom',
                    message: `Pattern has no capture group ${String(step.group)}.`,
                    path: ['group'],
                });
            }
        }),
    z
        .object({
            op: z.literal(IpxRbsValueTransformOp.REPLACE_TEXT),
            find: IpxRbsTextMatchSchema,
            replace_with: z.string().max(IpxRbsValueTransformLimits.MAX_LITERAL_LENGTH),
        })
        .strict(),
    z
        .object({ op: z.literal(IpxRbsValueTransformOp.REMOVE_TEXT), find: IpxRbsTextMatchSchema })
        .strict(),
    z.object({ op: z.literal(IpxRbsValueTransformOp.TRIM) }).strict(),
    z.object({ op: z.literal(IpxRbsValueTransformOp.COLLAPSE_SPACES) }).strict(),
    z
        .object({ op: z.literal(IpxRbsValueTransformOp.ADD_PREFIX), text: LiteralTextSchema })
        .strict(),
    z
        .object({ op: z.literal(IpxRbsValueTransformOp.ADD_SUFFIX), text: LiteralTextSchema })
        .strict(),
]);

export const IpxRbsValueTransformV1Schema: z.ZodType<IpxRbsValueTransformV1> = z
    .object({
        transform_version: z.literal(1),
        steps: z
            .array(IpxRbsValueTransformStepSchema)
            .min(1)
            .max(IpxRbsValueTransformLimits.MAX_STEPS),
    })
    .strict();

const IpxRbsValueTransformVersionSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'ipx-rbs-value-transform.transform_version',
    provenance: { kind: 'finite-integer-domain', values: IpxRbsValueTransformSupportedVersions },
});

const IpxRbsValueTransformCaptureGroupSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'ipx-rbs-value-transform.steps.group',
    provenance: {
        kind: 'finite-integer-domain',
        values: Array.from(
            { length: IpxRbsValueTransformLimits.MAX_CAPTURE_GROUP + 1 },
            (_unused: unknown, group: number): number => group
        ),
    },
});

const StoredTransformStepListSchema = z.object({ steps: z.array(z.unknown()) }).passthrough();

// Stored JSON reads numbers losslessly; turn the transform's two integer
// fields back into safe integers before the exact schema parses them.
export function normalizeStoredIpxRbsValueTransformNumbers(value: unknown): unknown {
    const versioned = normalizeStoredJsonSafeIntegerObjectFields(value, {
        safeIntegerDomain: IpxRbsValueTransformVersionSafeIntegerDomain,
        fields: ['transform_version'],
    });
    const stepList = StoredTransformStepListSchema.safeParse(versioned);
    if (!stepList.success) {
        return versioned;
    }
    return {
        ...stepList.data,
        steps: stepList.data.steps.map((step: unknown): unknown =>
            normalizeStoredJsonSafeIntegerObjectFields(step, {
                safeIntegerDomain: IpxRbsValueTransformCaptureGroupSafeIntegerDomain,
                fields: ['group'],
            })
        ),
    };
}

export type IpxRbsValueTransformResult =
    | { readonly status: 'ok'; readonly value: string; readonly trace: readonly string[] }
    | { readonly status: 'no_match'; readonly step_index: number }
    | { readonly status: 'empty_result' }
    | { readonly status: 'input_too_long' }
    | { readonly status: 'output_too_long' };

type CompiledRbsRegex =
    | { readonly status: 'compiled'; readonly regex: RE2JS }
    | { readonly status: 'invalid'; readonly message: string };

function compileRbsRegex(match: IpxRbsRegexTextMatch): CompiledRbsRegex {
    try {
        return {
            status: 'compiled',
            regex: RE2JS.compile(match.pattern, match.ignore_case ? RE2JS.CASE_INSENSITIVE : 0),
        };
    } catch (error: unknown) {
        return {
            status: 'invalid',
            message: error instanceof Error ? error.message : 'Pattern does not compile.',
        };
    }
}

interface TextMatchSpan {
    readonly start: number;
    readonly end: number;
    readonly groups: readonly (string | null)[];
}

// Every non-overlapping match, left to right, in linear time.
function findTextMatches(input: string, match: IpxRbsTextMatch): readonly TextMatchSpan[] {
    if (!match.is_regex) {
        const spans: TextMatchSpan[] = [];
        let from = input.indexOf(match.text);
        while (from !== -1) {
            spans.push({ start: from, end: from + match.text.length, groups: [match.text] });
            from = input.indexOf(match.text, from + match.text.length);
        }
        return spans;
    }
    const compiled = compileRbsRegex(match);
    if (compiled.status === 'invalid') {
        throw new TypeError(`Invalid RBS value transform pattern: ${compiled.message}`);
    }
    const matcher = compiled.regex.matcher(input);
    const spans: TextMatchSpan[] = [];
    while (matcher.find()) {
        const groups: (string | null)[] = [];
        for (let group = 0; group <= matcher.groupCount(); group += 1) {
            groups.push(matcher.group(group));
        }
        spans.push({ start: matcher.start(), end: matcher.end(), groups });
    }
    return spans;
}

function pickOccurrence(
    spans: readonly TextMatchSpan[],
    occurrence: IpxRbsValueTransformOccurrence
): TextMatchSpan | undefined {
    return occurrence === IpxRbsValueTransformOccurrence.FIRST ? spans[0] : spans.at(-1);
}

function replaceTextMatches(input: string, match: IpxRbsTextMatch, replacement: string): string {
    let output = '';
    let cursor = 0;
    for (const span of findTextMatches(input, match)) {
        output += input.slice(cursor, span.start) + replacement;
        cursor = span.end;
    }
    return output + input.slice(cursor);
}

function applyRbsValueTransformStep(input: string, step: IpxRbsValueTransformStep): string | null {
    switch (step.op) {
        case IpxRbsValueTransformOp.TEXT_BEFORE: {
            const span = pickOccurrence(findTextMatches(input, step.separator), step.occurrence);
            return span === undefined ? null : input.slice(0, span.start);
        }
        case IpxRbsValueTransformOp.TEXT_AFTER: {
            const span = pickOccurrence(findTextMatches(input, step.separator), step.occurrence);
            return span === undefined ? null : input.slice(span.end);
        }
        case IpxRbsValueTransformOp.EXTRACT_TEXT: {
            const span = pickOccurrence(findTextMatches(input, step.match), step.occurrence);
            return span?.groups[step.group] ?? null;
        }
        case IpxRbsValueTransformOp.REPLACE_TEXT:
            return replaceTextMatches(input, step.find, step.replace_with);
        case IpxRbsValueTransformOp.REMOVE_TEXT:
            return replaceTextMatches(input, step.find, '');
        case IpxRbsValueTransformOp.TRIM:
            return input.trim();
        case IpxRbsValueTransformOp.COLLAPSE_SPACES:
            return input.replace(/\s+/gu, ' ');
        case IpxRbsValueTransformOp.ADD_PREFIX:
            return step.text + input;
        case IpxRbsValueTransformOp.ADD_SUFFIX:
            return input + step.text;
    }
}

// Traceability: implements OBL-RBS-VR-CATALOG from implementation RBS-VALUE-TRANSFORM-APPLY
// Traceability: implements OBL-RBS-VR-REGEX from implementation RBS-VALUE-TRANSFORM-APPLY
export function applyRbsValueTransform(
    input: string,
    transform: IpxRbsValueTransformV1
): IpxRbsValueTransformResult {
    if (input.length > IpxRbsValueTransformLimits.MAX_INPUT_LENGTH) {
        return { status: 'input_too_long' };
    }
    const trace: string[] = [];
    let value = input;
    for (const [stepIndex, step] of transform.steps.entries()) {
        const next = applyRbsValueTransformStep(value, step);
        if (next === null) {
            return { status: 'no_match', step_index: stepIndex };
        }
        value = next;
        trace.push(value);
    }
    if (value.length === 0) {
        return { status: 'empty_result' };
    }
    if (value.length > IpxRbsValueTransformLimits.MAX_OUTPUT_LENGTH) {
        return { status: 'output_too_long' };
    }
    return { status: 'ok', value, trace };
}

function describeTextMatch(match: IpxRbsTextMatch): string {
    return match.is_regex
        ? `pattern /${match.pattern}/${match.ignore_case ? 'i' : ''}`
        : `"${match.text}"`;
}

// A short label for one rule, as RPA Builder and error messages show it.
export function describeRbsValueTransformStep(step: IpxRbsValueTransformStep): string {
    switch (step.op) {
        case IpxRbsValueTransformOp.TEXT_BEFORE:
            return `Keep text before ${step.occurrence} ${describeTextMatch(step.separator)}`;
        case IpxRbsValueTransformOp.TEXT_AFTER:
            return `Keep text after ${step.occurrence} ${describeTextMatch(step.separator)}`;
        case IpxRbsValueTransformOp.EXTRACT_TEXT:
            return `Keep ${step.group === 0 ? 'the match' : `group ${String(step.group)}`} of ${step.occurrence} ${describeTextMatch(step.match)}`;
        case IpxRbsValueTransformOp.REPLACE_TEXT:
            return `Replace ${describeTextMatch(step.find)} with "${step.replace_with}"`;
        case IpxRbsValueTransformOp.REMOVE_TEXT:
            return `Remove ${describeTextMatch(step.find)}`;
        case IpxRbsValueTransformOp.TRIM:
            return 'Trim spaces';
        case IpxRbsValueTransformOp.COLLAPSE_SPACES:
            return 'Collapse spaces';
        case IpxRbsValueTransformOp.ADD_PREFIX:
            return `Add "${step.text}" before`;
        case IpxRbsValueTransformOp.ADD_SUFFIX:
            return `Add "${step.text}" after`;
    }
}

// The text the failing rule ran on, for the operator who is looking at the
// same website: whitespace collapsed and long text shortened. Undefined for
// failures that are not about one rule's input.
export const RbsValueTransformFailedTextMaxLength = 60;

export function readRbsValueTransformFailedText(
    input: string,
    transform: IpxRbsValueTransformV1,
    result: Exclude<IpxRbsValueTransformResult, { readonly status: 'ok' }>
): string | undefined {
    if (result.status !== 'no_match') {
        return undefined;
    }
    let value = input;
    for (const step of transform.steps.slice(0, result.step_index)) {
        const next = applyRbsValueTransformStep(value, step);
        if (next === null) {
            return undefined;
        }
        value = next;
    }
    const collapsed = value.replace(/\s+/gu, ' ').trim();
    return collapsed.length <= RbsValueTransformFailedTextMaxLength
        ? collapsed
        : `${collapsed.slice(0, RbsValueTransformFailedTextMaxLength - 1).trimEnd()}\u2026`;
}

// The message shown when a chain fails: field, rule and what went wrong, and,
// when given, the text the rule ran on. Messages that may be logged are built
// without that text, because it is website content such as a patient's name.
export function describeRbsValueTransformFailure(
    fieldDisplayName: string,
    transform: IpxRbsValueTransformV1,
    result: Exclude<IpxRbsValueTransformResult, { readonly status: 'ok' }>,
    failedText?: string
): string {
    switch (result.status) {
        case 'no_match': {
            const step = transform.steps[result.step_index];
            const label = step === undefined ? 'a rule' : describeRbsValueTransformStep(step);
            const quoted = failedText === undefined ? '' : ` in "${failedText}"`;
            return `${fieldDisplayName}, rule ${String(result.step_index + 1)} (${label}) found no match${quoted}.`;
        }
        case 'empty_result':
            return `${fieldDisplayName}: the rules produced an empty value.`;
        case 'input_too_long':
            return `${fieldDisplayName}: the text is longer than ${String(IpxRbsValueTransformLimits.MAX_INPUT_LENGTH)} characters.`;
        case 'output_too_long':
            return `${fieldDisplayName}: the rules produced more than ${String(IpxRbsValueTransformLimits.MAX_OUTPUT_LENGTH)} characters.`;
    }
}

// A value transform failure travels from the RBS runtime to the payments
// sidebar as this code, followed by the message, in the workflow error text.
export const RBS_IPS_VALUE_TRANSFORM_FAILED_ERROR_CODE = 'RBS_IPS_VALUE_TRANSFORM_FAILED' as const;

// `message` names the field and rule and may be logged; `display_message`
// also quotes the text the rule ran on and goes only to the payments
// sidebar's modal.
export interface RbsIpsValueTransformFailureResult {
    readonly status: 'value_transform_failed';
    readonly errorCode: typeof RBS_IPS_VALUE_TRANSFORM_FAILED_ERROR_CODE;
    readonly message: string;
    readonly display_message: string;
}

export const RbsIpsValueTransformFailureResultSchema: z.ZodType<RbsIpsValueTransformFailureResult> =
    z
        .object({
            status: z.literal('value_transform_failed'),
            errorCode: z.literal(RBS_IPS_VALUE_TRANSFORM_FAILED_ERROR_CODE),
            message: z.string().min(1),
            display_message: z.string().min(1),
        })
        .strict();

const RbsIpsValueTransformFailureErrorPrefix = `${RBS_IPS_VALUE_TRANSFORM_FAILED_ERROR_CODE}: `;

export function formatRbsIpsValueTransformFailureError(message: string): string {
    return RbsIpsValueTransformFailureErrorPrefix + message;
}

// The error text with a value transform failure's message removed, for logs:
// the message quotes website text.
export function redactRbsIpsValueTransformFailureError(
    error: string | undefined
): string | undefined {
    return readRbsIpsValueTransformFailureError(error) === undefined
        ? error
        : RBS_IPS_VALUE_TRANSFORM_FAILED_ERROR_CODE;
}

// The message of a value transform failure, or undefined for any other error.
export function readRbsIpsValueTransformFailureError(
    error: string | undefined
): string | undefined {
    return error?.startsWith(RbsIpsValueTransformFailureErrorPrefix) === true
        ? error.slice(RbsIpsValueTransformFailureErrorPrefix.length)
        : undefined;
}
